import React from 'react';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import MoneyInput from '../../components/MoneyInput';

/**
 * Structured fee taxonomy for Douane / Logistics:
 *  - All fees: client_price (charged to client)
 *  - Transiteur ONLY: also tracks internal_amount (cost), so we can compute margin.
 *  - Clients NEVER see internal_amount — it's stripped from API responses for users
 *    without `can_see_purchase_price`. The Douane PO PDF never includes it.
 */
export const FEE_TYPES = [
  { key: 'dedouanement',      label: 'Frais de Dédouanement',           hasInternal: false },
  { key: 'transiteur',         label: 'Frais de Transiteur',              hasInternal: true  },
  { key: 'degroupeur',         label: 'Frais de Dégroupeur',              hasInternal: false },
  { key: 'transport_depannage',label: 'Frais de Transport / Dépannage',   hasInternal: false },
  { key: 'assurance',          label: 'Frais d\'Assurance',               hasInternal: false },
  { key: 'penalite_retard',    label: 'Pénalité de retard',               hasInternal: false },
  { key: 'magazinage',         label: 'Frais de Magazinage',              hasInternal: false },
];

const findType = (name) => FEE_TYPES.find(t => t.label === name || t.key === name);

const CustomFeesEditor = ({ items = [], onChange, canSeeInternal = false }) => {
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = (label) => onChange([...(items || []), { name: label, amount: 0, internal_amount: 0, is_paid: false }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const clientTotal = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const paidTotal   = items.filter(it => it.is_paid).reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
  const internalTotal = canSeeInternal
    ? items.reduce((s, it) => s + (parseFloat(it.internal_amount) || 0), 0)
    : 0;

  // Which fee types are not yet added (so we can suggest only unused ones)
  const usedNames = new Set(items.map(it => (it.name || '').trim()));
  const availableTypes = FEE_TYPES.filter(t => !usedNames.has(t.label));

  return (
    <div className="space-y-2" data-testid="custom-fees-editor">
      <p className="text-[10px] text-amber-700 italic bg-amber-50 border-l-2 border-amber-400 px-2 py-1">
        ℹ Billed separately on a <b>Douane PO</b> — not included in the Avis de Versement / order total.
      </p>

      {items.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">No fees yet — pick a fee type below to add one.</p>
      )}

      {items.map((it, idx) => {
        const t = findType(it.name);
        const showInternal = canSeeInternal && t?.hasInternal;
        return (
          <div key={`fee-${idx}`} className="border rounded bg-white p-2 space-y-1.5" data-testid={`fee-row-${idx}`}>
            <div className="flex items-center gap-2">
              <Select value={it.name || ''} onValueChange={(v) => update(idx, { name: v })}>
                <SelectTrigger className="h-7 text-xs flex-1" data-testid={`fee-name-${idx}`}>
                  <SelectValue placeholder="Select fee type" />
                </SelectTrigger>
                <SelectContent>
                  {FEE_TYPES.map(ft => (
                    <SelectItem key={ft.key} value={ft.label}>{ft.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1 text-[11px] whitespace-nowrap" title="Mark as paid">
                <input
                  type="checkbox"
                  checked={!!it.is_paid}
                  onChange={(e) => update(idx, { is_paid: e.target.checked })}
                  data-testid={`fee-paid-${idx}`}
                />
                Paid
              </label>
              <button type="button" onClick={() => remove(idx)} className="text-destructive text-base px-1 hover:underline" data-testid={`fee-remove-${idx}`}>×</button>
            </div>
            <div className={showInternal ? 'grid grid-cols-2 gap-2' : ''}>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Client price (DZD)</label>
                <MoneyInput
                  value={it.amount}
                  onChange={(v) => update(idx, { amount: v })}
                  placeholder="0"
                  className="h-7 text-sm"
                  data-testid={`fee-amount-${idx}`}
                />
              </div>
              {showInternal && (
                <div className="bg-purple-50/40 border border-purple-200 rounded -m-px p-px">
                  <label className="text-[10px] text-purple-700 uppercase tracking-wider px-1">
                    Internal cost (DZD) <span className="font-bold">· hidden from client</span>
                  </label>
                  <MoneyInput
                    value={it.internal_amount}
                    onChange={(v) => update(idx, { internal_amount: v })}
                    placeholder="0"
                    className="h-7 text-sm bg-purple-50/40"
                    data-testid={`fee-internal-${idx}`}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap gap-1 pt-1">
        {availableTypes.length > 0 ? (
          availableTypes.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => add(t.label)}
              className="text-[10px] px-2 py-0.5 border rounded-full hover:bg-primary hover:text-primary-foreground transition"
              data-testid={`fee-preset-${t.key}`}
            >
              + {t.label}
            </button>
          ))
        ) : (
          <span className="text-[10px] text-muted-foreground italic">All standard fee types added.</span>
        )}
      </div>

      {clientTotal > 0 && (
        <div className="border-t pt-1.5 text-[11px] flex items-center justify-between flex-wrap gap-1">
          <span>
            <span className="text-muted-foreground">Client total: </span>
            <span className="font-bold text-foreground">{clientTotal.toLocaleString()} DZD</span>
            {paidTotal > 0 && <span className="ml-2 text-green-600">· Paid {paidTotal.toLocaleString()}</span>}
          </span>
          {canSeeInternal && internalTotal > 0 && (
            <span className="text-purple-700 font-medium" data-testid="fee-margin">
              Internal: {internalTotal.toLocaleString()} · Margin: <b>{(clientTotal - internalTotal).toLocaleString()} DZD</b>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomFeesEditor;
